<template>
	<div
		v-if="hasBanner"
		class="organization-profile-status-banner"
		:class="bannerClass"
		role="status"
	>
		<strong>{{ bannerLabel }}</strong>
		— {{ bannerMessage }}
	</div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import {
	normalizeOrganizationProfileStatusFromApi,
	ORGANIZATION_PROFILE_STATUSES,
	ORGANIZATION_STRINGS,
} from '@/config/settingsConstants'

const VALID_STATUSES = Object.values(ORGANIZATION_PROFILE_STATUSES)

const STATUS_DATE_FORMAT = 'MMMM DD, YYYY [at] h:mm A'

/**
 * Organization profile status banner for the settings organization section.
 */
export default {
	computed: {
		bannerClass() {
			return `organization-profile-status-banner--${this.normalizedStatus}`
		},

		bannerLabel() {
			return this.getBannerCopy().label
		},

		bannerMessage() {
			return this.buildBannerMessage()
		},

		hasBanner() {
			return VALID_STATUSES.includes(this.normalizedStatus)
		},

		normalizedStatus() {
			return normalizeOrganizationProfileStatusFromApi(this.status)
		},

		partnerTimezone() {
			return this.partner?.timezone || 'America/New_York'
		},

		...mapGetters({
			partner: 'partners/active',
		}),
	},

	methods: {
		buildBannerMessage() {
			const copy = this.getBannerCopy()
			const {
				NOT_STARTED,
				DRAFT,
				PENDING_REVIEW,
				IN_REVIEW,
				VERIFIED,
				REJECTED,
				FAILED,
			} = ORGANIZATION_PROFILE_STATUSES

			switch (this.normalizedStatus) {
				case NOT_STARTED:
					return copy.message

				case DRAFT: {
					const relativeTime = this.meta.savedAt
						? moment(this.meta.savedAt).fromNow()
						: ''
					const editorName = this.meta.savedBy || ''
					let lastEditedMessage = ''

					if (relativeTime && editorName) {
						lastEditedMessage = ORGANIZATION_STRINGS.draft.lastEdited(
							relativeTime,
							editorName
						)
					} else if (relativeTime) {
						lastEditedMessage = `Last edited ${relativeTime}.`
					} else if (editorName) {
						lastEditedMessage = `Last edited by ${editorName}.`
					}

					return [lastEditedMessage, copy.messageSuffix]
						.filter(Boolean)
						.join(' ')
				}

				case PENDING_REVIEW: {
					const submittedAt = this.formatStatusDate(
						this.meta.submittedAt
					)
					const submittedBy = this.meta.submittedBy || ''
					let prefix = copy.messagePrefix

					if (submittedBy && submittedAt) {
						prefix = `Submitted by ${submittedBy} on ${submittedAt}`
					} else if (submittedAt) {
						prefix = `${copy.messagePrefix} ${submittedAt}`
					} else if (submittedBy) {
						prefix = `Submitted by ${submittedBy}`
					}

					return [
						prefix.endsWith('.') ? prefix : `${prefix}.`,
						copy.messageSuffix,
					].join(' ')
				}

				case IN_REVIEW:
					return copy.message

				case VERIFIED: {
					const verifiedAt = this.formatStatusDate(
						this.meta.verifiedAt
					)

					return [
						verifiedAt
							? `${copy.messagePrefix} ${verifiedAt}.`
							: copy.messagePrefix,
						copy.messageSuffix,
					].join(' ')
				}

				case REJECTED:
					return this.meta.rejectionMessage || copy.defaultMessage

				case FAILED:
					return this.meta.failureMessage || copy.defaultMessage

				default:
					return ''
			}
		},

		formatStatusDate(value) {
			if (!value) {
				return ''
			}

			return moment(value)
				.tz(this.partnerTimezone)
				.format(STATUS_DATE_FORMAT)
		},

		getBannerCopy() {
			const { statusBanner } = ORGANIZATION_STRINGS

			switch (this.normalizedStatus) {
				case ORGANIZATION_PROFILE_STATUSES.NOT_STARTED:
					return statusBanner.notStarted

				case ORGANIZATION_PROFILE_STATUSES.DRAFT:
					return statusBanner.draft

				case ORGANIZATION_PROFILE_STATUSES.PENDING_REVIEW:
					return statusBanner.pendingReview

				case ORGANIZATION_PROFILE_STATUSES.IN_REVIEW:
					return statusBanner.inReview

				case ORGANIZATION_PROFILE_STATUSES.VERIFIED:
					return statusBanner.verified

				case ORGANIZATION_PROFILE_STATUSES.REJECTED:
					return statusBanner.rejected

				case ORGANIZATION_PROFILE_STATUSES.FAILED:
					return statusBanner.failed

				default:
					return {
						label: '',
						message: '',
					}
			}
		},
	},
	name: 'OrganizationProfileStatusBanner',

	props: {
		meta: {
			default: () => ({}),
			type: Object,
		},

		status: {
			default: '',
			type: String,
		},
	},
}
</script>
